import React from 'react'
import { useIntl } from 'react-intl'

import { Column, Layout, Row, Rows, Section } from '@/components/layout'
import { Text } from '@/components/common'
import BuyTry from '@/components/blocks/BuyTry'
import TextImageRow from '@/components/sections/TextImageRow'
import BoxedInfo from '@/components/blocks/BoxedInfo'
import MobileAppFeatures from '@/components/blocks/MobileAppFeatures'
import PricingMain from '@/components/blocks/PricingMain'
import FaqSection from '@/components/blocks/FaqSection'
import multipleWaysToScan from '@/assets/filing-cabinet/multiple-ways-to-scan.jpg'
import organizeDocs from '@/assets/filing-cabinet/organize-doc.jpg'
import autoFilingDocs from '@/assets/filing-cabinet/auto-filing-docs.jpg'
import mobileApp from '@/assets/filing-cabinet/mobile-app.jpg'

import styles from './digital-filing-cabinet.module.scss'

const pageId = 'digitalFilingCabinet'

const appFeatures = [
  {
    description: 'Capture documents from anywhere',
    moreInfo: 'Quickly snap a picture of any paper document. Then upload them straight to Neat using the app. ',
  },
  {
    description: 'Access documents from anywhere',
    moreInfo: 'At any moment you might need an important document to use. Using our Neat app allows you to quickly find them easily.',
  },
]

const benefits = [
  {
    title: 'Save time filing and searching',
    description: 'Reduce your time filing your important documents by automating your digital filing system with guided workflows. Neat\'s powerful search functionality allows you to find these uploaded documents instantly. Quickly locate files using keywords, dates, or categories.',
  },
  {
    title: 'Easily share to anyone to have access',
    description: 'From significant others, business partners to financial & legal advisors you can share specific documents with anyone you provide permission to from anywhere. Share comments, important files and folders to collaborate with your trusted partners.',
  },
  {
    title: 'Keep your information secure & safe',
    description: 'Protect from natural disasters or cyber threats. Neat has compliant data centers in which they monitor 24/7/365 and perform numerous audits. Have bank-level encryption to keep your digital filing cabinet secured. Privacy is at the utmost priority and you will not have to worry about your files being shared or sold.',
  },
]

const faqs = ['cabinetUsedFor', 'accessOnPhone', 'storagePerMonth', 'scannersToUse', 'supportAvailable']

export default function DigitalFilingCabinet() {
  const { formatMessage } = useIntl()
  const getPageMessage = (id) => formatMessage({ id: `pages.${pageId}.${id}` })

  return (
    <Layout.Content className={styles.trackReceipts}>
      <Section bgForeground paddingVertical={0} wrapperClassName={styles.wrapper}>
        <Column className="w-full">
          <div className={styles.heroHolder}>
            <div className="max-w-700">
              <Text bold brandSecondary className={styles.tagline} noMargin size="large" type="display">Digital Filing Cabinet</Text>
              <Text.H1 bold normal onTint size="medium">Virtual Document Management</Text.H1>
              <Text.P onTint>Start a paperless lifestyle and organize all of your documents in a safe & secured cloud based app platform.</Text.P>
              <BuyTry justifyStart marginTop={32} noOutline />
              <Text.P onTint size="xsmall">Neat is a yearly subscription where you can add on solutions.</Text.P>
            </div>
          </div>
        </Column>
      </Section>

      <Section className={styles.receiptManagement} paddingBottom={24}>
        <Rows gap={0}>
          <Row center paddingBottom={24} textCenter>
            <h2 className="c-brand">Say goodbye to bulky file cabinets, chaotic hard drives, and paper stacks.</h2>
          </Row>

          <TextImageRow
            image={multipleWaysToScan}
            reverse
            text={getPageMessage('features.multipleWaysToScan')}
            title="Multiple ways to easily scan any of your documents"
          />
          <TextImageRow
            image={organizeDocs}
            text={getPageMessage('features.organizeDoc')}
            title="Organize every document your way"
          />
          <TextImageRow
            image={autoFilingDocs}
            reverse
            text={getPageMessage('features.autoFilingDocs')}
            title="Automate filing documents instead of manual entry"
          />
        </Rows>
      </Section>

      <Section center>
        <Text.H2 center>Go paperless with your own digital filing cabinet</Text.H2>
        <BuyTry noOutline />
        <Text.P>Neat is a yearly subscription where you can add on solutions.</Text.P>
      </Section>

      <BoxedInfo boxes={benefits} sectionTitle="The benefits of having a digital filing cabinet" />

      <MobileAppFeatures appFeatures={appFeatures} image={mobileApp} title="Your digital filing cabinet close by everyday with our mobile app" />

      <Section bgBrandTertiary style={{ backgroundColor: '#E9E8FF' }}>
        <PricingMain hasBottomContent />
      </Section>

      <FaqSection faqs={faqs} pageId={pageId} />
    </Layout.Content>
  )
}

DigitalFilingCabinet.layoutProps = {
  pageId,
}
